import type Database from '@tauri-apps/plugin-sql'
import { isTauri } from './platform'

/**
 * قاعدة SQLite المحلية داخل Tauri.
 * تحمل نسخة من الكتالوج والأرصدة، وطابور العمليات التي لم تُزامَن بعد.
 */

const SCHEMA = [
  `CREATE TABLE IF NOT EXISTS meta (
     key TEXT PRIMARY KEY,
     value TEXT NOT NULL
   )`,

  `CREATE TABLE IF NOT EXISTS categories (
     id TEXT PRIMARY KEY,
     nameAr TEXT NOT NULL,
     parentId TEXT,
     sortOrder INTEGER NOT NULL DEFAULT 0,
     isActive INTEGER NOT NULL DEFAULT 1,
     deletedAt TEXT
   )`,

  `CREATE TABLE IF NOT EXISTS products (
     id TEXT PRIMARY KEY,
     sku TEXT NOT NULL,
     barcode TEXT,
     nameAr TEXT NOT NULL,
     nameFr TEXT,
     searchKey TEXT NOT NULL,
     type TEXT NOT NULL,
     categoryId TEXT,
     unit TEXT NOT NULL,
     costPrice INTEGER NOT NULL DEFAULT 0,
     retailPrice INTEGER NOT NULL DEFAULT 0,
     taxRate INTEGER NOT NULL DEFAULT 0,
     minStock INTEGER NOT NULL DEFAULT 0,
     trackStock INTEGER NOT NULL DEFAULT 1,
     isActive INTEGER NOT NULL DEFAULT 1,
     deletedAt TEXT
   )`,
  `CREATE INDEX IF NOT EXISTS idx_products_barcode ON products(barcode)`,
  `CREATE INDEX IF NOT EXISTS idx_products_search ON products(searchKey)`,

  `CREATE TABLE IF NOT EXISTS inventory_levels (
     productId TEXT PRIMARY KEY,
     quantity INTEGER NOT NULL DEFAULT 0
   )`,

  /* الطابور: كل عملية أُنشئت محلياً وتنتظر الوصول إلى الخادم.
     الحمولة تُخزَّن كاملة كـ JSON حتى تبقى قابلة للإرسال كما هي بعد أيام. */
  `CREATE TABLE IF NOT EXISTS outbox (
     id TEXT PRIMARY KEY,
     kind TEXT NOT NULL,
     payload TEXT NOT NULL,
     createdAt TEXT NOT NULL,
     attempts INTEGER NOT NULL DEFAULT 0,
     lastError TEXT,
     /* pending | failed — الناجح يُحذف، ولا يبقى إلا ما ينتظر أو ما رفضه الخادم */
     status TEXT NOT NULL DEFAULT 'pending'
   )`,
  `CREATE INDEX IF NOT EXISTS idx_outbox_status ON outbox(status, createdAt)`,

  /* نسخة محلية من الفواتير المُنشأة على هذا الجهاز — لإعادة الطباعة دون اتصال */
  `CREATE TABLE IF NOT EXISTS local_sales (
     id TEXT PRIMARY KEY,
     reference TEXT NOT NULL,
     total INTEGER NOT NULL,
     occurredAt TEXT NOT NULL,
     payload TEXT NOT NULL,
     synced INTEGER NOT NULL DEFAULT 0
   )`,
]

let dbPromise: Promise<Database> | null = null

async function connect(): Promise<Database> {
  const { default: Database } = await import('@tauri-apps/plugin-sql')
  const db = await Database.load('sqlite:najah-pos.db')
  for (const statement of SCHEMA) {
    await db.execute(statement)
  }
  return db
}

export function getDb(): Promise<Database> {
  if (!isTauri()) {
    return Promise.reject(new Error('قاعدة البيانات المحلية متاحة في تطبيق سطح المكتب فقط'))
  }
  dbPromise ??= connect()
  return dbPromise
}

/* ── meta ── */
export async function getMeta(key: string): Promise<string | null> {
  const db = await getDb()
  const rows = await db.select<{ value: string }[]>('SELECT value FROM meta WHERE key = $1', [key])
  return rows[0]?.value ?? null
}

export async function setMeta(key: string, value: string): Promise<void> {
  const db = await getDb()
  await db.execute(
    'INSERT INTO meta (key, value) VALUES ($1, $2) ON CONFLICT(key) DO UPDATE SET value = excluded.value',
    [key, value],
  )
}

/* ── الطابور ── */
export interface OutboxRow {
  id: string
  kind: 'sale' | 'return'
  payload: string
  createdAt: string
  attempts: number
  lastError: string | null
  status: 'pending' | 'failed'
}

export async function enqueue(id: string, kind: OutboxRow['kind'], payload: unknown): Promise<void> {
  const db = await getDb()
  await db.execute(
    `INSERT INTO outbox (id, kind, payload, createdAt) VALUES ($1, $2, $3, $4)
     ON CONFLICT(id) DO NOTHING`,
    [id, kind, JSON.stringify(payload), new Date().toISOString()],
  )
}

export async function pendingOutbox(limit = 100): Promise<OutboxRow[]> {
  const db = await getDb()
  return db.select<OutboxRow[]>(
    `SELECT * FROM outbox WHERE status = 'pending' ORDER BY createdAt ASC LIMIT $1`,
    [limit],
  )
}

export async function countPending(): Promise<{ pending: number; failed: number }> {
  const db = await getDb()
  const rows = await db.select<{ status: string; n: number }[]>(
    'SELECT status, COUNT(*) as n FROM outbox GROUP BY status',
  )
  return {
    pending: rows.find((r) => r.status === 'pending')?.n ?? 0,
    failed: rows.find((r) => r.status === 'failed')?.n ?? 0,
  }
}

export async function markSynced(ids: string[]): Promise<void> {
  if (!ids.length) return
  const db = await getDb()
  const placeholders = ids.map((_, i) => `$${i + 1}`).join(',')
  await db.execute(`DELETE FROM outbox WHERE id IN (${placeholders})`, ids)
  await db.execute(`UPDATE local_sales SET synced = 1 WHERE id IN (${placeholders})`, ids)
}

/**
 * فشل رفضه الخادم صراحةً (بيانات فاسدة): يُعلَّم failed ويخرج من الطابور،
 * وإلا أعاد الجهاز محاولته إلى الأبد وعلقت خلفه كل الفواتير اللاحقة.
 */
export async function markFailed(id: string, error: string): Promise<void> {
  const db = await getDb()
  await db.execute(
    `UPDATE outbox SET status = 'failed', attempts = attempts + 1, lastError = $2 WHERE id = $1`,
    [id, error],
  )
}

/** فشل مؤقّت (شبكة/خادم): يبقى في الطابور ويُعاد لاحقاً */
export async function markRetry(id: string, error: string): Promise<void> {
  const db = await getDb()
  await db.execute('UPDATE outbox SET attempts = attempts + 1, lastError = $2 WHERE id = $1', [id, error])
}

export async function failedOutbox(): Promise<OutboxRow[]> {
  const db = await getDb()
  return db.select<OutboxRow[]>(`SELECT * FROM outbox WHERE status = 'failed' ORDER BY createdAt DESC`)
}

export async function retryFailed(id: string): Promise<void> {
  const db = await getDb()
  await db.execute(`UPDATE outbox SET status = 'pending', lastError = NULL WHERE id = $1`, [id])
}

/* ── الفواتير المحلية ── */
export async function saveLocalSale(sale: { id: string; reference: string; total: number; occurredAt: string }, payload: unknown) {
  const db = await getDb()
  await db.execute(
    `INSERT INTO local_sales (id, reference, total, occurredAt, payload) VALUES ($1, $2, $3, $4, $5)
     ON CONFLICT(id) DO NOTHING`,
    [sale.id, sale.reference, sale.total, sale.occurredAt, JSON.stringify(payload)],
  )
}

/** عدّاد الفواتير اليومي لهذا الجهاز — يُغذّي رقم الفاتورة المقروء */
export async function nextSaleCounter(): Promise<number> {
  const today = new Date().toISOString().slice(0, 10)
  const db = await getDb()
  const rows = await db.select<{ n: number }[]>(
    'SELECT COUNT(*) as n FROM local_sales WHERE substr(occurredAt, 1, 10) = $1',
    [today],
  )
  return (rows[0]?.n ?? 0) + 1
}
